import { useQuery } from 'react-query';
import * as SettingApis from 'apis/setting';
import { AxiosError } from 'axios';
import { PRETAX_ACCOUNT_V2_TYPES } from 'constants/accounts';

export type ICompany = SettingApis.FetchProfileResponse['company'];
export type IEmployee = SettingApis.FetchProfileResponse['employee'];
export type IEmployeeWallet =
  SettingApis.FetchProfileResponse['employee']['employee_wallets'][0];
export type ICompanyWalletConfiguration =
  SettingApis.FetchProfileResponse['company']['company_wallet_configurations'][0];
export type IEmployeeSettings =
  SettingApis.FetchProfileResponse['employee']['settings'];
export type IWalletType =
  SettingApis.FetchProfileResponse['employee']['employee_wallets'][0]['wallet_type'];
export type ICompanyLocation =
  SettingApis.FetchProfileResponse['company']['settings']['location'][0];
export type ICategory =
  SettingApis.FetchProfileResponse['employee']['employee_wallets'][0]['company_wallet_configuration']['categories'][0];

const getProfile = async () => {
  const {
    data: { company, employee },
  } = await SettingApis.fetchProfile();
  return { company, employee };
};

const getProfileV3 = async ({
  isAlegeusHSAFeatureFlag,
}: {
  isAlegeusHSAFeatureFlag: boolean;
}) => {
  // In /v3/settings/profile, we returns posttax & pretax accounts that
  // including inactive accounts too.
  const {
    data: { company, employee },
  } = await SettingApis.fetchProfileV3();
  /*
   * In Phase 2 (1/1/2023 - 3/2/2023) of PRT-2330, this can be removed to allow Forma HSA to flow through
   */
  if (isAlegeusHSAFeatureFlag) {
    const employeeWalletsWithNoFormaHSA = employee.employee_wallets.filter(
      (w) => w.wallet_type_id !== PRETAX_ACCOUNT_V2_TYPES.HSA
    );
    const mutatedEmployeeObj = {
      ...employee,
      employee_wallets: employeeWalletsWithNoFormaHSA,
    };
    return { company, employee: mutatedEmployeeObj };
  }

  return { company, employee };
};

const useQueryProfile = ({
  isEnabled,
  use_v3_profile = false,
  onSuccess,
  onError,
  isAlegeusHSAFeatureFlag = false,
}: {
  isEnabled: boolean;
  use_v3_profile?: boolean;
  onSuccess?: (payload: { employee: IEmployee; company: ICompany }) => void;
  onError?: (e: AxiosError) => void;
  isAlegeusHSAFeatureFlag?: boolean;
}) => {
  const {
    data,
    isLoading: is_loading,
    refetch,
  } = useQuery(
    ['getProfile', use_v3_profile],
    () =>
      use_v3_profile ? getProfileV3({ isAlegeusHSAFeatureFlag }) : getProfile(),
    {
      enabled: isEnabled,
      onSuccess,
      onError,
    }
  );

  return {
    data,
    is_loading,
    refetch,
  };
};

export default useQueryProfile;
